import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Cookie, X } from "lucide-react";

export const CONSENT_KEY = "cnp_cookie_consent";

/** Returns "all" | "necessary" | null */
export function getConsent() {
  try { return localStorage.getItem(CONSENT_KEY); } catch { return null; }
}

/**
 * Dispatched (as a `window` CustomEvent) whenever consent changes so any
 * analytics or third-party script can react in real-time without a reload.
 */
export const CONSENT_EVENT = "cnp:consent-change";

function setConsent(value) {
  try {
    localStorage.setItem(CONSENT_KEY, value);
    window.dispatchEvent(new CustomEvent(CONSENT_EVENT, { detail: value }));
  } catch {
    /* ignore */
  }
}

export default function CookieBanner() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Delay a beat so the banner doesn't compete with initial paint / hero animations.
    const t = setTimeout(() => {
      if (!getConsent()) setVisible(true);
    }, 500);
    return () => clearTimeout(t);
  }, []);

  if (!visible) return null;

  const accept = () => { setConsent("all"); setVisible(false); };
  const reject = () => { setConsent("necessary"); setVisible(false); };

  return (
    <div
      role="dialog"
      aria-live="polite"
      aria-label="Consentimento de cookies"
      data-testid="cookie-banner"
      className="fixed z-[80] left-3 right-3 bottom-3 sm:left-6 sm:right-auto sm:bottom-6 sm:max-w-md rounded-2xl border border-stone-200 bg-white/95 backdrop-blur-md shadow-[0_18px_60px_rgba(16,60,74,0.18)] p-5 sm:p-6"
    >
      <div className="flex items-start gap-3">
        <div className="hidden sm:flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#103C4A]/10 text-[#103C4A]">
          <Cookie className="h-5 w-5" />
        </div>
        <div className="flex-1">
          <div className="text-[10px] uppercase tracking-[0.22em] text-stone-500">Cookies</div>
          <p className="mt-1.5 text-sm text-stone-700 leading-relaxed">
            Usamos cookies essenciais ao funcionamento do site e, com o seu consentimento, cookies de analítica
            para percebermos o que resulta melhor. Não usamos publicidade nem partilhamos dados para esse fim.{" "}
            <Link to="/politica-de-privacidade" className="underline hover:text-[#103C4A]" data-testid="cookie-banner-policy">
              Saber mais
            </Link>
            .
          </p>
          <div className="mt-4 flex flex-wrap gap-2">
            <button type="button" onClick={accept} data-testid="cookie-accept-all" className="btn-primary text-sm">
              Aceitar todos
            </button>
            <button type="button" onClick={reject} data-testid="cookie-reject" className="btn-secondary text-sm">
              Só necessários
            </button>
          </div>
        </div>
        <button
          type="button"
          onClick={reject}
          aria-label="Fechar"
          data-testid="cookie-banner-close"
          className="text-stone-400 hover:text-stone-700 -mt-1 -mr-1 p-1"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
